/* import __COLOCATED_TEMPLATE__ from './data-fields.hbs'; */
/* eslint-disable @intercom/intercom/no-bare-strings */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { type UserField, type TicketField } from 'embercom/objects/standalone/data-fields/common';
import { action } from '@ember/object';
import type Store from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import type Admin from 'embercom/models/admin';
import { post } from 'embercom/lib/ajax';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import type IntlService from 'ember-intl/services/intl';

interface Args {
  hideBorder?: boolean;
  hideLeftPane?: boolean;
}

type App = { id: string; currentAdmin: Admin; reload: () => void };
type NotificationsService = {
  notifyResponseError: (
    e: Error,
    messages: { default: string },
    options: { responseProvidedErrors: number[] },
  ) => void;
};

export default class StandaloneSalesforceDataFields extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare notificationsService: NotificationsService;
  @service declare store: Store;
  @service declare intl: IntlService;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.finStandaloneService.salesforceDataLoader.loadSalesforceDataFields).perform();
  }

  get app(): App {
    return this.finStandaloneService.app;
  }

  get salesforceConfig() {
    return this.finStandaloneService.salesforceConfig;
  }

  get synchronizedUserFields(): Array<UserField> {
    return this.finStandaloneService.salesforceDataFields.userFields.filter(
      (field) => field.required || field.intercom_cda?.identifier || field.isSynchronizing,
    );
  }

  get unsynchronizedUserFields(): Array<UserField> {
    return this.finStandaloneService.salesforceDataFields.userFields.filter(
      (field) => !field.required && !field.intercom_cda?.identifier && !field.isSynchronizing,
    );
  }

  get synchronizedCaseFields(): Array<TicketField> {
    return this.finStandaloneService.salesforceDataFields.caseFields.filter(
      (field) => field.required || field.intercom_cvda?.id || field.isSynchronizing,
    );
  }

  get unsynchronizedCaseFields(): Array<TicketField> {
    return this.finStandaloneService.salesforceDataFields.caseFields.filter(
      (field) => !field.required && !field.intercom_cvda?.id && !field.isSynchronizing,
    );
  }

  @action async addSalesforceUserField(userField: UserField) {
    try {
      // Place field at the end of the list
      this.finStandaloneService.salesforceDataFields.userFields.removeObject(userField);
      this.finStandaloneService.salesforceDataFields.userFields.pushObject(userField);

      userField.isSynchronizing = true;
      let intercomCda = await post(
        `/ember/standalone/salesforce_data/${userField.id}/synchronize_user_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      userField.intercom_cda = intercomCda;
      this.app.reload();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: 'Something went wrong and we couldn’t synchronize that field.',
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    } finally {
      userField.isSynchronizing = false;
    }
  }

  @action async addSalesforceCaseField(caseField: TicketField) {
    try {
      // Place the field at the end of the list
      this.finStandaloneService.salesforceDataFields.caseFields.removeObject(caseField);
      this.finStandaloneService.salesforceDataFields.caseFields.pushObject(caseField);

      caseField.isSynchronizing = true;
      let intercomCvda = await post(
        `/ember/standalone/salesforce_data/${caseField.id}/synchronize_case_field`,
        {
          app_id: this.app.id,
          admin_id: this.app.currentAdmin.id,
        },
      );
      caseField.intercom_cvda = intercomCvda;
      this.app.reload();
    } catch (e) {
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t('standalone.notifications.fin-service-errors.field-sync-error'),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
    } finally {
      caseField.isSynchronizing = false;
    }
  }

  @tracked selectedField?: UserField | TicketField | ExternalFieldIdentifier;

  @action didCloseModal() {
    this.selectedField = undefined;
    this.finStandaloneService.notifyPropertyChange('salesforceDataFields');
  }

  @action selectField(field: UserField | TicketField | ExternalFieldIdentifier) {
    this.selectedField = field;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::DataFields': typeof StandaloneSalesforceDataFields;
    'standalone/Salesforce/DataFields': typeof StandaloneSalesforceDataFields;
  }
}
